/**
 * Shared input component styles
 * Used by TextInputWithLabel, SelectWithLabel, and other input components
 */

export const baseInputClasses = `
  w-full px-4
  bg-white border rounded-md outline-none
  font-normal
  transition-all duration-150
  placeholder:text-gray-400 placeholder:font-normal
  disabled:cursor-not-allowed disabled:hover:bg-gray-300 disabled:focus:bg-gray-300
`;

export const errorInputClasses = `
  border-red-300
  hover:border-red-400
  focus:border-red-400 focus:ring-2 focus:ring-red-100
`;

export const normalInputClasses = `
  border-gray-200
  hover:border-blue-300
  focus:border-blue-400 focus:ring-2 focus:ring-blue-100
`;

/**
 * Get complete input classes based on error state
 */
export const getInputClasses = (
	hasError: boolean,
	additionalClasses = ""
): string => {
	return `
  py-4
    ${baseInputClasses}
    ${hasError ? errorInputClasses : normalInputClasses}
    ${additionalClasses}
  `.trim();
};

/**
 * Compact variant — same colours/borders, smaller height for inline use
 */
export const getCompactInputClasses = (
	hasError: boolean,
	additionalClasses = ""
): string => {
	return `
    py-1.5
    ${baseInputClasses}
    ${hasError ? errorInputClasses : normalInputClasses}
    ${additionalClasses}
  `.trim();
};

/**
 * React-Select classNames configuration
 * Uses the same styling approach as TextInput
 */
export const getReactSelectClassNames = (
	hasError: boolean,
	hasValue: boolean,
	disabled?: boolean,
	height?: string,
	isMulti?: boolean
) => ({
	container: () => "",
	control: (state: any) => `
  flex items-center ${height || "py-4"} ${baseInputClasses}
    ${hasError ? errorInputClasses : normalInputClasses}
    ${
			state.isFocused
				? hasError
					? "border-red-400 ring-2 ring-red-100"
					: "border-blue-400 ring-2 ring-blue-100"
				: ""
		}
  `,
	valueContainer: () =>
		isMulti ? "flex flex-wrap gap-1" : "flex items-center",
	input: () => "text-gray-700",
	placeholder: () => "text-gray-400",
	singleValue: () => (hasValue ? "text-gray-700" : "text-gray-400"),
	menu: () => "absolute w-full mt-1 border border-gray-200 rounded-md bg-white shadow-lg z-10",
	menuList: () => "py-1 max-h-[300px] overflow-y-auto",
	option: (state: any) => `
    px-4 py-2 cursor-pointer
    ${
			state.isSelected
				? "bg-blue-500 text-white"
				: state.isFocused
					? "bg-blue-50 text-gray-700"
					: "text-gray-700"
		}
    ${state.isSelected ? "" : "hover:bg-blue-100"}
  `,
	dropdownIndicator: () => "text-blue-500",
	indicatorSeparator: () => "hidden",
	clearIndicator: () => "text-gray-400 hover:text-gray-600 cursor-pointer",
	multiValue: () =>
		"flex items-center bg-blue-50 border border-blue-200 rounded px-2 py-0.5 mr-1 my-0.5",
	multiValueLabel: () => "text-gray-700 text-sm",
	multiValueRemove: () =>
		"ml-1 text-gray-400 hover:text-red-500 cursor-pointer",
});

/**
 * Wrapper container classes for input components
 */
export const getInputWrapperClasses = (
	hasError: boolean,
	noMargin?: boolean,
	additionalClasses = ""
): string => {
	return `
    relative transition-all duration-150
    ${hasError ? "pb-2" : ""}
    ${noMargin ? "!mb-0" : "mb-4"}
    ${additionalClasses}
  `.trim();
};
